import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, addDoc, updateDoc, doc, getDoc, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useBranchContext } from '@/contexts/BranchContext';
import type { Class, Enrollment } from '@/types';
import { useAuditLog } from '@/hooks/useAuditLog';

export const useClasses = (targetUserId?: string) => {
    const { currentUser } = useAuth();
    const { selectedBranch } = useBranchContext();
    const { logAction } = useAuditLog();
    const [allClasses, setAllClasses] = useState<Class[]>([]);
    const [classes, setClasses] = useState<Class[]>([]);
    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [loading, setLoading] = useState(true);

    const effectiveUserId = targetUserId || currentUser?.uid;

    // Real-time subscription for classes
    useEffect(() => {
        // ... (existing classes fetch logic unchanged)
        setLoading(true);

        // Demo mode

        // Real Firebase mode
        let q;
        if (selectedBranch) {
            q = query(
                collection(db, 'classes'),
                where('status', '==', 'active'),
                where('branchId', '==', selectedBranch)
            );
        } else {
            q = query(
                collection(db, 'classes'),
                where('status', '==', 'active')
            );
        }

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const classesData: Class[] = [];
            querySnapshot.forEach((doc) => {
                classesData.push({ id: doc.id, ...doc.data() } as Class);
            });

            // Sort manually
            classesData.sort((a, b) => {
                const dayA = a.schedules?.[0]?.dayOfWeek ?? 0;
                const dayB = b.schedules?.[0]?.dayOfWeek ?? 0;
                return dayA - dayB;
            });

            console.log('Fetched classes via snapshot:', classesData);
            setAllClasses(classesData);
            setLoading(false);
        }, (error) => {
            console.error('Error listening to classes:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [selectedBranch]);

    // Filter classes by selected branch
    useEffect(() => {
        if (selectedBranch === null) {
            setClasses(allClasses);
        } else {
            setClasses(allClasses.filter(c => c.branchId === selectedBranch));
        }
    }, [selectedBranch, allClasses]);

    // Real-time subscription for enrolments (TARGET USER)
    useEffect(() => {
        if (!effectiveUserId) {
            setEnrollments([]);
            return;
        }


        const q = query(
            collection(db, 'enrollments'),
            where('userId', '==', effectiveUserId),
            where('status', '==', 'active')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const enrollmentsData: Enrollment[] = [];
            querySnapshot.forEach((doc) => {
                enrollmentsData.push({ id: doc.id, ...doc.data() } as Enrollment);
            });
            setEnrollments(enrollmentsData);
        }, (error) => {
            console.error('Error listening to enrollments:', error);
        });

        return () => unsubscribe();
    }, [effectiveUserId, currentUser]);

    const enrollInClass = async (classId: string) => {
        if (!currentUser) return;

        try {

            const classDoc = await getDoc(doc(db, 'classes', classId));
            if (!classDoc.exists()) return;

            const classData = classDoc.data() as Class;

            // Check for existing active enrollment to prevent duplicates
            const existingEnrollmentQuery = query(
                collection(db, 'enrollments'),
                where('userId', '==', effectiveUserId),
                where('classId', '==', classId),
                where('status', '==', 'active')
            );
            const existingEnrollments = await getDocs(existingEnrollmentQuery);
            if (!existingEnrollments.empty) {
                console.log('User already enrolled in this class, skipping duplicate enrollment');
                return; // Already enrolled, don't create duplicate
            }

            await addDoc(collection(db, 'enrollments'), {
                userId: effectiveUserId,
                userName: currentUser.displayName,
                classId,
                className: classData.name,
                enrolledAt: new Date(),
                status: 'active'
            });

            await updateDoc(doc(db, 'classes', classId), {
                enrolled: (classData.enrolled || 0) + 1
            });
        } catch (error) {
            console.error('Error enrolling in class:', error);
            throw error;
        }
    };

    const unenrollFromClass = async (enrollmentId: string) => {
        try {

            const enrollmentDoc = await getDoc(doc(db, 'enrollments', enrollmentId));
            if (!enrollmentDoc.exists()) return;

            const enrollmentData = enrollmentDoc.data() as Enrollment;

            await updateDoc(doc(db, 'enrollments', enrollmentId), {
                status: 'cancelled'
            });

            const classDoc = await getDoc(doc(db, 'classes', enrollmentData.classId));
            if (classDoc.exists()) {
                const classData = classDoc.data() as Class;
                await updateDoc(doc(db, 'classes', enrollmentData.classId), {
                    enrolled: Math.max(0, (classData.enrolled || 0) - 1)
                });
            }
        } catch (error) {
            console.error('Error unenrolling from class:', error);
            throw error;
        }
    };

    const createClass = async (classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt' | 'enrolled'>) => {
        try {

            console.log('Creating class with data:', classData);
            const docRef = await addDoc(collection(db, 'classes'), {
                ...classData,
                enrolled: 0,
                createdAt: new Date(),
                updatedAt: new Date()
            });

            return { id: docRef.id, ...classData };
        } catch (error) {
            console.error('Error creating class:', error);
            throw error;
        }
    };

    const updateClass = async (classId: string, classData: Partial<Omit<Class, 'id' | 'createdAt' | 'updatedAt'>>) => {
        try {

            await updateDoc(doc(db, 'classes', classId), {
                ...classData,
                updatedAt: new Date()
            });

            // Audit Log
            logAction({
                action: 'update',
                module: 'class',
                targetId: classId,
                targetName: classData.name,
                details: JSON.stringify(Object.keys(classData))
            });
        } catch (error) {
            console.error('Error updating class:', error);
            throw error;
        }
    };

    const deleteClass = async (classId: string) => {
        try {

            await updateDoc(doc(db, 'classes', classId), {
                status: 'deleted',
                updatedAt: new Date()
            });

            // Audit Log
            logAction({
                action: 'delete',
                module: 'class',
                targetId: classId
            });
        } catch (error) {
            console.error('Error deleting class:', error);
            throw error;
        }
    };

    const deleteClasses = async (classIds: string[]) => {
        try {

            // Execute all deletions in parallel
            await Promise.all(classIds.map(id =>
                updateDoc(doc(db, 'classes', id), {
                    status: 'deleted',
                    updatedAt: new Date()
                })
            ));

            // Audit Log
            logAction({
                action: 'bulk_delete',
                module: 'class',
                details: JSON.stringify({ classIds, count: classIds.length })
            });
        } catch (error) {
            console.error('Error deleting classes:', error);
            throw error;
        }
    };

    const isEnrolled = (classId: string) => {
        return enrollments.some(e => e.classId === classId && e.status === 'active');
    };

    return {
        classes,
        enrollments,
        loading,
        enrollInClass,
        unenrollFromClass,
        isEnrolled,
        createClass,
        updateClass,
        deleteClass,
        deleteClasses,
        fetchClassEnrollments: useCallback(async (classId: string) => {
            // ... (keep existing fetchClassEnrollments logic)

            try {
                const q = query(
                    collection(db, 'enrollments'),
                    where('classId', '==', classId),
                    where('status', '==', 'active')
                );
                const querySnapshot = await getDocs(q);
                const enrollmentsData: Enrollment[] = [];
                const seenUserIds = new Set<string>();

                querySnapshot.forEach((docSnap) => {
                    const enrollment = { id: docSnap.id, ...docSnap.data() } as Enrollment;
                    // Deduplicate by userId - keep first occurrence
                    if (!seenUserIds.has(enrollment.userId)) {
                        seenUserIds.add(enrollment.userId);
                        enrollmentsData.push(enrollment);
                    } else {
                        console.warn(`Duplicate enrollment found for userId ${enrollment.userId} in class ${classId}`);
                    }
                });
                return enrollmentsData;
            } catch (error) {
                console.error('Error fetching class enrollments:', error);
                return [];
            }
        }, []),

        // Admin function to enroll any user in a class
        enrollUserInClass: useCallback(async (userId: string, userName: string, classId: string) => {
            try {
                const classToEnroll = allClasses.find(c => c.id === classId);
                if (!classToEnroll) throw new Error('Class not found');


                // Check for existing active enrollment to prevent duplicates
                const existingEnrollmentQuery = query(
                    collection(db, 'enrollments'),
                    where('userId', '==', userId),
                    where('classId', '==', classId),
                    where('status', '==', 'active')
                );
                const existingEnrollments = await getDocs(existingEnrollmentQuery);
                if (!existingEnrollments.empty) {
                    console.log('User already enrolled in this class, skipping duplicate enrollment');
                    // Return the existing enrollment instead of throwing
                    const existingDoc = existingEnrollments.docs[0];
                    return { id: existingDoc.id, ...existingDoc.data() } as Enrollment;
                }

                const docRef = await addDoc(collection(db, 'enrollments'), {
                    userId,
                    userName,
                    classId,
                    className: classToEnroll.name,
                    enrolledAt: new Date(),
                    status: 'active'
                });

                await updateDoc(doc(db, 'classes', classId), {
                    enrolled: (classToEnroll.enrolled || 0) + 1
                });

                return { id: docRef.id, userId, userName, classId, className: classToEnroll.name, enrolledAt: new Date(), status: 'active' as const };
            } catch (error) {
                console.error('Error enrolling user in class:', error);
                throw error;
            }
        }, [allClasses]),

        // Get enrollments for a specific user
        getEnrollmentsForUser: useCallback(async (userId: string): Promise<Enrollment[]> => {

            try {
                const q = query(
                    collection(db, 'enrollments'),
                    where('userId', '==', userId),
                    where('status', '==', 'active')
                );
                const querySnapshot = await getDocs(q);
                const userEnrollments: Enrollment[] = [];
                querySnapshot.forEach((doc) => {
                    userEnrollments.push({ id: doc.id, ...doc.data() } as Enrollment);
                });
                return userEnrollments;
            } catch (error) {
                console.error('Error fetching user enrollments:', error);
                return [];
            }
        }, [enrollments]),

        // Admin function to unenroll a user
        unenrollUser: useCallback(async (enrollmentId: string) => {
            try {

                const enrollmentDoc = await getDoc(doc(db, 'enrollments', enrollmentId));
                if (!enrollmentDoc.exists()) return;

                const enrollmentData = enrollmentDoc.data() as Enrollment;

                await updateDoc(doc(db, 'enrollments', enrollmentId), {
                    status: 'cancelled'
                });

                const classDoc = await getDoc(doc(db, 'classes', enrollmentData.classId));
                if (classDoc.exists()) {
                    const classData = classDoc.data() as Class;
                    await updateDoc(doc(db, 'classes', enrollmentData.classId), {
                        enrolled: Math.max(0, (classData.enrolled || 0) - 1)
                    });
                }
            } catch (error) {
                console.error('Error unenrolling user:', error);
                throw error;
            }
        }, [enrollments])
    };
};
